import React, { useEffect, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Typography,
  FormHelperText
} from '@mui/material';

const periods = [
  { value: '1m', label: '1m' },
  { value: '3m', label: '3m' },
  { value: '6m', label: '6m' },
  { value: '12m', label: '12m' },
  { value: 'custom', label: 'Custom' },
];

/**
 * GoalsFilterBar Component
 *
 * Filter bar for goal-aligned reports with:
 * - Organization selector (admin only)
 * - Time period selector
 * - Goal template multi-select (1-3 templates, loaded from backend)
 */
export default function GoalsFilterBar({
  filters,
  onFilterChange,
  selectedGoals,
  onGoalsChange,
  organizations = [],
  accessLevel = 'user'
}) {
  const aiEvaluationGoal = useMemo(() => ({
    template_id: 'combined',
    template_name: 'AI Evaluation (BETA VERSION)',
    goal_text: 'Automated AI evaluation combining organizational skills, culture, friction, and roadmap insights.'
  }), []);

  useEffect(() => {
    if (!selectedGoals.some((g) => g.template_id === 'combined')) {
      onGoalsChange([aiEvaluationGoal]);
    }
  }, [aiEvaluationGoal, onGoalsChange, selectedGoals]);

  const handleFilterChange = (key) => (e) => {
    onFilterChange({ ...filters, [key]: e.target.value });
  };

  const isOrgSelected = Boolean(filters.orgId);
  const canGenerate = isOrgSelected && selectedGoals.length >= 1;
  const actionableGoalTemplate = selectedGoals[0] || aiEvaluationGoal;

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
        {/* Period Selector */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select label="Period" value={filters.period || '3m'} onChange={handleFilterChange('period')}>
            {periods.map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom Date Range */}
        {filters.period === 'custom' && (
          <>
            <TextField
              size="small"
              type="date"
              label="Start"
              InputLabelProps={{ shrink: true }}
              value={filters.startDate || ''}
              onChange={handleFilterChange('startDate')}
            />
            <TextField
              size="small"
              type="date"
              label="End"
              InputLabelProps={{ shrink: true }}
              value={filters.endDate || ''}
              onChange={handleFilterChange('endDate')}
            />
          </>
        )}

        {/* Organization Selector - Admin only */}
        {accessLevel === 'admin' && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Organization *</InputLabel>
            <Select
            label="Organization *"
            value={filters.orgId || ''}
            onChange={handleFilterChange('orgId')}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
            {!isOrgSelected && (
              <FormHelperText error>Required</FormHelperText>
            )}
          </FormControl>
        )}

        <Box sx={{ flexGrow: 1 }} />
      </Stack>

      {/* Generation Status Helper */}
      {!canGenerate && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {!isOrgSelected && accessLevel === 'admin' && '⚠️ Select an organization to continue'}
          {isOrgSelected && selectedGoals.length === 0 && '⚠️ Loading AI Evaluation configuration…'}
        </Typography>
      )}
    </Box>
  );
}
